/**
 * Public pay page — what an end-customer sees when they click the
 * "Pay Now" link in an invoice email. Co-branded with the subscriber's
 * business name and logo at top, "Powered by CountBucks" footer.
 *
 * Routes:
 *  /pay/:token              - main pay page
 *  /pay/:token/success      - post-Checkout confirmation
 *
 * No authentication — the unguessable URL token IS the auth.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  CreditCard,
  Lock,
  Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
}

function PoweredByFooter() {
  return (
    <div className="text-center text-xs text-zinc-500 dark:text-zinc-400 py-6 space-y-1" data-testid="powered-by-footer">
      <div className="flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3" />
        <span>Secured by Stripe · Powered by CountBucks</span>
      </div>
      <a
        href="https://countbucks.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
      >
        countbucks.com
      </a>
    </div>
  );
}

function Header({ merchant }) {
  return (
    <header className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
      <div className="max-w-2xl mx-auto px-4 py-5 flex items-center gap-3">
        {merchant?.logo_url ? (
          <img
            src={merchant.logo_url}
            alt={merchant.name}
            className="w-10 h-10 rounded-lg object-cover"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
            style={{ background: merchant?.brand_color || '#10b981' }}
          >
            <Building2 className="w-5 h-5" />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
            {merchant?.name || 'Your accountant'}
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">Invoice payment</div>
        </div>
      </div>
    </header>
  );
}

export default function PublicPayPage() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/pay/${token}`);
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          if (!cancelled) setError(data.detail || 'Could not load invoice');
        } else {
          const data = await r.json();
          if (!cancelled) setInvoice(data);
        }
      } catch (e) {
        if (!cancelled) setError('Network error — please try again');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handlePay = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/public/pay/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok || !data.url) {
        setError(data.detail || 'Could not start payment');
        setSubmitting(false);
        return;
      }
      // Hard redirect to Stripe Checkout.
      window.location.href = data.url;
    } catch (e) {
      setError('Network error — please try again');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
        <Header merchant={{}} />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-8 text-center" data-testid="public-pay-error">
            <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-2">We couldn't load this invoice</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">{error || 'The link may be invalid or expired.'}</p>
          </div>
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  const isPaid = invoice.is_paid;

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
      <Header merchant={invoice.merchant} />
      <div className="flex-1 max-w-2xl w-full mx-auto p-4 sm:p-6 space-y-4">
        {/* Hero card with amount */}
        <div
          className="rounded-2xl p-6 sm:p-8 text-white shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${invoice.merchant?.brand_color || '#10b981'}, ${invoice.merchant?.brand_color || '#10b981'}dd)`,
          }}
          data-testid="public-pay-hero"
        >
          <div className="text-xs uppercase tracking-wide opacity-90 mb-1">Amount due</div>
          <div className="text-4xl sm:text-5xl font-bold">
            {formatCurrency(invoice.balance_due, invoice.currency)}
          </div>
          <div className="text-xs mt-2 opacity-90">
            Invoice #{invoice.invoice_no}
            {invoice.due_date && ` · Due ${invoice.due_date}`}
          </div>
        </div>

        {isPaid ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/15 p-6 text-center" data-testid="public-pay-already-paid">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 mb-1">
              This invoice is paid in full
            </h3>
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              No further action needed — thanks!
            </p>
          </div>
        ) : invoice.payment_enabled ? (
          <Button
            onClick={handlePay}
            disabled={submitting}
            className="w-full h-14 text-base font-semibold"
            style={{
              background: invoice.merchant?.brand_color || '#10b981',
            }}
            data-testid="public-pay-button"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting to Stripe...</>
            ) : (
              <><CreditCard className="w-5 h-5 mr-2" /> Pay {formatCurrency(invoice.balance_due, invoice.currency)} now</>
            )}
          </Button>
        ) : (
          <div className="rounded-xl border border-amber-500/30 bg-amber-50 dark:bg-amber-900/15 p-4 text-sm" data-testid="public-pay-disabled">
            <p className="text-amber-900 dark:text-amber-200">
              {invoice.payment_disabled_reason || 'Online payments are not enabled for this invoice.'}
            </p>
          </div>
        )}

        {/* Line items */}
        {invoice.line_items?.length > 0 && (
          <div className="rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 text-xs uppercase tracking-wide text-zinc-500">
              Details
            </div>
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {invoice.line_items.map((li, i) => (
                <li key={i} className="px-4 py-3 flex items-baseline gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{li.description || '—'}</div>
                    {li.quantity && Number(li.quantity) !== 1 && (
                      <div className="text-xs text-zinc-500">
                        {li.quantity} × {formatCurrency(li.unit_price || 0, invoice.currency)}
                      </div>
                    )}
                  </div>
                  <div className="font-medium tabular-nums">
                    {formatCurrency(li.amount || 0, invoice.currency)}
                  </div>
                </li>
              ))}
              <li className="px-4 py-3 flex items-baseline gap-3 text-sm font-semibold bg-zinc-50 dark:bg-zinc-900/50">
                <div className="flex-1">Total</div>
                <div className="tabular-nums">{formatCurrency(invoice.total, invoice.currency)}</div>
              </li>
            </ul>
          </div>
        )}
      </div>
      <PoweredByFooter />
    </div>
  );
}

/**
 * Success page — shown after Stripe Checkout redirects back with a session_id.
 * Polls /session/{sid} to confirm payment_status=paid before showing the
 * "thanks" message; otherwise shows "processing" with a refresh hint.
 */
export function PublicPaySuccessPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking');
  const [details, setDetails] = useState(null);
  const [merchant, setMerchant] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let cancelled = false;

    (async () => {
      // Fire both in parallel so the merchant header renders alongside the
      // session status check.
      const [invR, sessR] = await Promise.all([
        fetch(`${API_URL}/api/public/pay/${token}`),
        fetch(`${API_URL}/api/public/pay/${token}/session/${sessionId}`),
      ]);
      if (cancelled) return;
      try {
        const inv = await invR.json();
        setMerchant(inv.merchant);
      } catch { /* ignore */ }
      try {
        const sess = await sessR.json();
        setDetails(sess);
        if (sess.payment_status === 'paid') setState('paid');
        else if (sess.status === 'complete') setState('paid');
        else setState('processing');
      } catch {
        setState('processing');
      }
    })();
    return () => { cancelled = true; };
  }, [token, sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
      <Header merchant={merchant || {}} />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-8 text-center">
          {state === 'paid' && (
            <>
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" data-testid="public-pay-success-title">Payment received</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">
                Thank you! Your payment has been sent to {merchant?.name || 'the business'}.
              </p>
              {details?.amount_total > 0 && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Amount: <strong>{formatCurrency(details.amount_total / 100, details.currency || 'USD')}</strong>
                </p>
              )}
              <p className="text-xs text-zinc-500 mt-4">
                A receipt has been emailed to you.
              </p>
            </>
          )}
          {state === 'processing' && (
            <>
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-zinc-400" />
              <h2 className="text-lg font-semibold mb-2">Confirming your payment...</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                This usually takes a few seconds.
              </p>
            </>
          )}
          {state === 'checking' && (
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
          )}
          {state === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                We couldn't confirm your payment. Please contact the business directly.
              </p>
            </>
          )}
        </div>
      </div>
      <PoweredByFooter />
    </div>
  );
}
